import { BluetoothDevice } from './../models/BluetoothDevice';
import { BluetoothSerial } from '@ionic-native/bluetooth-serial';
import { defineStore } from 'pinia';
import { AndroidPermissions } from '@awesome-cordova-plugins/android-permissions';
import { Device } from '@capacitor/device';
import { useFeedbackStore } from './feedback';
import mitt from 'mitt';

type Events = {
  'bluetooth-scan': string;
  'bluetooth-connect': boolean;
};

//Setup store
export const useBluetoothStore = defineStore('bluetooth', {
  state: () => ({
    bluetoothSerial: BluetoothSerial,
    btDevices: [] as BluetoothDevice[],
    selectedBtDevice: '',
    btEnabled: false,
    btConnected: false,
    emitter: mitt<Events>(),
    subscription: null as ReturnType<ReturnType<typeof BluetoothSerial["subscribe"]>["subscribe"]> | null
  }),
  actions: {
    async checkPermissions() {
      const feedbackStore = useFeedbackStore();

      //Default permissions
      const permissions = [
        AndroidPermissions.PERMISSION.BLUETOOTH_ADMIN
      ];

      //Check device information for adding additional permissions
      const deviceInfo = await Device.getInfo();


      //Check if operating system is android and add the right permission according to osVersion
      if (deviceInfo.operatingSystem == 'android') {
        if (parseInt(deviceInfo.osVersion) > 11) {
          //Permission for above android 11 that's required to connect to already paired devices
          permissions.unshift(AndroidPermissions.PERMISSION.BLUETOOTH_CONNECT);
          permissions.unshift(AndroidPermissions.PERMISSION.BLUETOOTH_SCAN);
        }
        else {
          //Permission needed in versions before 11 to use bluetooth pairing
          permissions.unshift(AndroidPermissions.PERMISSION.BLUETOOTH);
          permissions.unshift(AndroidPermissions.PERMISSION.ACCESS_FINE_LOCATION);
        }
      } else {
        this.btEnabled = false;
        return;
      }

      // Check which permissions have been granted
      const statuses = await Promise.all(
        permissions.map((permission) => AndroidPermissions.checkPermission(permission))
      );

      // Filter out the permissions that have not been granted
      const ungrantedPermissions = permissions.filter((permission, index) => !statuses[index].hasPermission);

      // Request the ungranted permissions, if no ungratedPermissions btEnabled to true
      if (ungrantedPermissions.length > 0) {
        feedbackStore.setTranslatedMessage('warning', 'btPermissionMissing');
        const status = await AndroidPermissions.requestPermissions(ungrantedPermissions);
        if (status.hasPermission) {
          this.btEnabled = true
          feedbackStore.setTranslatedMessage('success', 'btPermissionGranted');
        }
      } else {
        this.btEnabled = true;
      }
    },
    async getDevices() {
      if (!this.btEnabled) {
        return;
      }
      this.btDevices = [];
      this.bluetoothSerial.list().then((devices) => {
        devices.forEach((device: BluetoothDevice) => {
          this.btDevices.push({ name: device.name, address: device.address })
        })
        if (this.selectedBtDevice == '') {
          this.selectedBtDevice = this.btDevices[0].address;
        }
      }, (error) => {
        console.error(error);
      });
    },
    //Toggle function for connection
    async btToggleConnection() {
      const feedbackStore = useFeedbackStore();
      //Disconnect if connected
      if (this.btConnected) {
        this.bluetoothSerial.disconnect();
        this.btConnected = false;
        feedbackStore.setTranslatedMessage('warning', 'btDisconnectSuccess');
      }
      else {
        if (this.selectedBtDevice) {
          await this.connectToBtDevice(this.selectedBtDevice);
          this.emitter.emit('bluetooth-connect', true);
        }
      }
    },
    checkConnection() {
      const feedbackStore = useFeedbackStore();
      if (this.btConnected) {
        this.bluetoothSerial.isConnected().then((c) => {
          if (!c) {
            this.btConnected = false;
            if (this.subscription) {
              this.subscription.unsubscribe();
            }
            feedbackStore.setTranslatedMessage('danger', 'btConnectFail');
          }
        })
      }
    },
    async connectToBtDevice(address: string) {
      const feedbackStore = useFeedbackStore();

      await this.checkPermissions();

      this.bluetoothSerial.connect(address).subscribe(() => {
        this.btConnected = true;
        feedbackStore.setTranslatedMessage('success', 'btConnectSuccess')
        // Subscribe to data received from the RFID reader
        if (this.subscription) {
          this.subscription.unsubscribe();
        }
        this.subscription = this.bluetoothSerial.subscribe('\n').subscribe((data) => {
          this.emitter.emit('bluetooth-scan', data);
        }, (error: any) => {
          console.error(error);
        })
      }, (error: string) => {
        console.error('Failed to connect to RFID reader: ' + error);
        feedbackStore.setTranslatedMessage('danger', 'btConnectFail');
      });
    },
  },
  getters: {

  }
});